import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Tabs, Text, NotificationBadge } from '@frontend/design-system';
import { LocationAccordionList } from '../../../../components/location-accordion-list';
import { useSchedulingLocationInfo } from '../../../../hooks';
import { useScheduleBookingRequestsShallowStore } from '../../../../stores/use-schedule-booking-requests-store';
import { schedulePulseSelectedTabStorage, SchedulePulseTab } from '../../../../utils';
import { useScheduleActionsContext } from './ScheduleActionsContext';
import { ScheduleAlerts } from './ScheduleAlerts';
import { ScheduleMassMessages } from './ScheduleMassMessages/ScheduleMassMessages';
import { ScheduleRequestList } from './ScheduleRequests/ScheduleRequestList';
import { ScheduleRequestTabHeader } from './ScheduleRequests/ScheduleRequestTabHeader';

export const ScheduleActionsTabContent = () => {
  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  const { t } = useTranslation('schedule-pulse');
  const [controlledTab, setControlledTab] = useState<SchedulePulseTab>(SchedulePulseTab.AppointmentList);
  const { scheduleAlertsTotalCount, scheduleRequestsCountByLocation, isAppointmentRequestCountLoading } =
    useScheduleActionsContext();

  const { selectedLocationIds, getScopeName } = useAppScopeStore();
  const { getCFLocationMap } = useSchedulingLocationInfo();
  const onlineSchedulingCFLocationMap = getCFLocationMap('onlinescheduling');

  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { setCombineBookingRequests } = useScheduleBookingRequestsShallowStore('setCombineBookingRequests');

  /**TODO: RCM team, update the logic inside useGetWeaveInsuranceVerificationCustomizationFlagDetails to check for enhanced eligibility  */
  const { isFeatureHiddenInAllLocation } = useGetWeaveInsuranceVerificationCustomizationFlagDetails();

  const isRequestTabEnabled = Object.values(onlineSchedulingCFLocationMap).some((value) => !!value);
  const scheduleRequestLocationInfoList = useMemo(() => {
    if (!isRequestTabEnabled) return [];

    // filter locations that have scheduling flag enabled
    return selectedLocationIds
      .filter((locationId) => !!onlineSchedulingCFLocationMap?.[locationId])
      .map((locationId) => ({
        locationId,
        name: getScopeName(locationId),
        count: scheduleRequestsCountByLocation.find((item) => item.locationId === locationId)?.total ?? 0,
      }));
  }, [
    isRequestTabEnabled,
    selectedLocationIds,
    onlineSchedulingCFLocationMap,
    getScopeName,
    scheduleRequestsCountByLocation,
  ]);

  const scheduleRequestTotalCount = scheduleRequestLocationInfoList.reduce((acc, { count }) => acc + count, 0);

  useMemo(() => {
    const storageSelectedTab = schedulePulseSelectedTabStorage.get();
    const selectedTab =
      (!isRequestTabEnabled && storageSelectedTab === SchedulePulseTab.AppointmentRequests
        ? SchedulePulseTab.AppointmentList
        : storageSelectedTab) || SchedulePulseTab.AppointmentList;

    setControlledTab(selectedTab);
  }, [isRequestTabEnabled]);

  const handleTabChange = useCallback((id: string) => {
    setControlledTab(id as SchedulePulseTab);
    schedulePulseSelectedTabStorage.set(id as SchedulePulseTab);
  }, []);

  // Set combineBookingRequests to true if the v3 flag is enabled
  useEffect(() => {
    setCombineBookingRequests(!!isScheduleV3FlagEnabled);
  }, [isScheduleV3FlagEnabled, setCombineBookingRequests]);

  return (
    <Tabs controlledTab={controlledTab} onChange={handleTabChange}>
      <Tabs.Bar css={{ padding: theme.spacing(0, 3) }}>
        <Tabs.Tab trackingId={`${SchedulePulseTab.AppointmentList}-panel`} id={SchedulePulseTab.AppointmentList}>
          {t('Appointment List')}
        </Tabs.Tab>
        {isRequestTabEnabled && (
          <Tabs.Tab
            trackingId={`${SchedulePulseTab.AppointmentRequests}-panel`}
            id={SchedulePulseTab.AppointmentRequests}
          >
            <ScheduleRequestTabHeader count={scheduleRequestTotalCount} isLoading={isAppointmentRequestCountLoading} />
          </Tabs.Tab>
        )}
        {/**TODO: Remove debug mode once the feature is ready */}
        {!isFeatureHiddenInAllLocation && isDebugModeOn && (
          <Tabs.Tab trackingId={`${SchedulePulseTab.Insurance}-panel`} id={SchedulePulseTab.Insurance}>
            {t('Insurance')}
          </Tabs.Tab>
        )}

        <Tabs.Tab trackingId={`${SchedulePulseTab.Alerts}-panel`} id={SchedulePulseTab.Alerts}>
          <span css={[tabStyles, { gap: theme.spacing(1) }]}>
            {t('Alerts')}
            {!!scheduleAlertsTotalCount && <NotificationBadge>{scheduleAlertsTotalCount}</NotificationBadge>}
          </span>
        </Tabs.Tab>
      </Tabs.Bar>
      <Tabs.Panel css={tabListPanelContainerStyles} controller={SchedulePulseTab.AppointmentList}>
        <ScheduleMassMessages />
      </Tabs.Panel>
      {isRequestTabEnabled && (
        <Tabs.Panel css={tabListPanelContainerStyles} controller={SchedulePulseTab.AppointmentRequests}>
          <LocationAccordionList
            locationInfoList={scheduleRequestLocationInfoList}
            ContentComponent={ScheduleRequestList}
            minBodyHeight={381}
          />
        </Tabs.Panel>
      )}
      <Tabs.Panel css={tabListPanelContainerStyles} controller={SchedulePulseTab.Alerts}>
        <ScheduleAlerts />
      </Tabs.Panel>

      <Tabs.Panel controller={SchedulePulseTab.Insurance}>
        {/**TODO: Implement Insurance tab section */}
        <Text>{t('Insurance')}</Text>
      </Tabs.Panel>
    </Tabs>
  );
};

const tabStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const tabListPanelContainerStyles = css({
  padding: theme.spacing(3),
  backgroundColor: theme.colors.neutral5,
  height: '100%',
  overflowY: 'auto',
});
